import { useEffect } from 'react';
import { useAuth } from '@/providers/authProvider';
import { flushEngagement, SCREEN_NAMES, setEngagementSender, track } from '@/lib/engagement';
import { getPreviewSession } from '@/lib/preview';

const SESSION_KEY = 'zeo-companion-engagement-session';

/**
 * Records the app being opened and each screen shown. Never during "Preview
 * in Companion": that's an Admin looking, not the user.
 */
export function useEngagementTracking(email: string | undefined, screen: keyof typeof SCREEN_NAMES) {
  const { getAccessToken } = useAuth();
  const enabled = !!email && !getPreviewSession();

  useEffect(() => {
    if (!enabled) {
      setEngagementSender(null);
      return;
    }
    setEngagementSender(async (events) => {
      const token = await getAccessToken();
      // keepalive lets the last batch finish when the tab is closing.
      return fetch(`${import.meta.env.VITE_API_URL}/api/engagement/events/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ events }),
        keepalive: true,
      });
    });
    const onHide = () => {
      if (document.visibilityState === 'hidden') flushEngagement();
    };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      flushEngagement();
      setEngagementSender(null);
    };
  }, [enabled, getAccessToken]);

  // One visit per browser tab per person: sign-ins last 30 days, so "opened
  // the app" is the honest visit count.
  useEffect(() => {
    if (!enabled || !email) return;
    try {
      if (sessionStorage.getItem(SESSION_KEY) === email) return;
      sessionStorage.setItem(SESSION_KEY, email);
    } catch {
      // sessionStorage unavailable; count the visit anyway.
    }
    track('session_start');
  }, [enabled, email]);

  useEffect(() => {
    if (enabled) track('page_view', SCREEN_NAMES[screen]);
  }, [enabled, screen]);
}
